import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@material-ui/core";
import { getGroupPlanDetails } from "../../api/groupApi";

// ─── Styles ───────────────────────────────────────────────────────────────────

const headerCardStyle = {
  background: "#0078d4",
  color: "white",
  padding: "20px",
  borderRadius: "10px",
  marginBottom: "20px",
};

const sectionStyle = {
  padding: "20px",
  border: "1px solid #e0e0e0",
  borderRadius: "6px",
  backgroundColor: "#fafbfd",
  marginTop: "32px",
};

const sectionHeadingStyle = {
  color: "#0056a3",
  marginTop: 0,
  marginBottom: "16px",
};

const actionsStyle = {
  marginTop: "20px",
  display: "flex",
  gap: "15px",
  flexWrap: "wrap",
};

// ─── Mock data ────────────────────────────────────────────────────────────────

const MockGroupData = {
  groupName: "ABC Manufacturing",
  groupNumber: "123456",
  planYear: new Date().getFullYear().toString(),
  effectiveDate: "01/01/2026",
  groupStatus: "Active",
  enrollmentType: "Electronic",
  planBenefitStructure: "$10 / $25 / $40 / 25%",
  totalPremium: "$165.50",
  billingMethod: "Split Bill",
  openEnrollmentDate: "11/01/2026",
};

// ─── Component ────────────────────────────────────────────────────────────────

const GroupDashboard = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const groupNumber = searchParams.get("groupNumber") ?? "";
  //const groupState = searchParams.get("groupState") ?? "";

  const [group, setGroup] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!groupNumber) {
      setNotFound(true);
      setLoading(false);
      return;
    }

    //setGroup(MockGroupData); // TODO: remove when API is ready

    getGroupPlanDetails(groupNumber)
      .then((data) => {
        if (!data) {
          setNotFound(true);
        } else {
          setGroup(data);
        }
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [groupNumber]);

  if (loading) {
    return (
      <div
        role="main"
        className="main-container pt-md-5 pb-5 container js-quickedit-main-content"
      >
        <p>Loading...</p>
      </div>
    );
  }

  if (notFound) {
    return (
      <div
        role="main"
        className="main-container pt-md-5 pb-5 container js-quickedit-main-content"
      >
        <div className="row d-md-flex">
          <section className="col-sm-12 internal-page">
            <div className="region region-content">
              <section className="block block-clearstone-updates block-clearstone-breadcrumbs-block clearfix">
                <div className="breadcrumb-wrap">
                  <div className="region--breadcrumb nst-1">
                    <section className="block-system block-system-breadcrumb-block">
                      <div role="navigation">
                        <ol className="breadcrumb">
                          <li><Link to="/">Home</Link></li>
                          <li><Link to="/GroupPlanManagement">Group Plan Management</Link></li>
                          <li>Group Dashboard</li>
                        </ol>
                      </div>
                    </section>
                  </div>
                </div>
              </section>
              <h1 className="page-header">Group Not Found</h1>
              <p>No group was found for the provided Group Number and State. Please <Link to="/GroupLogin">try again</Link>.</p>
            </div>
          </section>
        </div>
      </div>
    );
  }

  return (
    <div
      role="main"
      className="main-container pt-md-5 pb-5 container js-quickedit-main-content"
    >
      <div className="row d-md-flex">
        <section className="col-sm-12 internal-page">
          <div className="region region-content">

            {/* Breadcrumb */}
            <section className="block block-clearstone-updates block-clearstone-breadcrumbs-block clearfix">
              <div className="breadcrumb-wrap">
                <div className="region--breadcrumb nst-1">
                  <section className="block-system block-system-breadcrumb-block">
                    <div role="navigation">
                      <ol className="breadcrumb">
                        <li><Link to="/">Home</Link></li>
                        <li><Link to="/GroupPlanManagement">Group Plan Management</Link></li>
                        <li>Group Dashboard</li>
                      </ol>
                    </div>
                  </section>
                </div>
              </div>
            </section>

            {/* Group Header */}
            <div style={headerCardStyle}>
              <h2 style={{ margin: 0 }}>{group.groupLegalName}</h2>
              <div>Group Number: <strong>{groupNumber}</strong></div>
            </div>

            {/* General Info */}
            <div style={sectionStyle}>
              <h2 style={sectionHeadingStyle}>General Group Information</h2>
              <div className="row form-group">
                <div className="col-sm-6 col-md-6 form-item">
                  <label className="control-label">Plan Year</label>
                  <p className="form-control-static">{group.planYear}</p>
                </div>
                <div className="col-sm-6 col-md-6 form-item">
                  <label className="control-label">Effective Date</label>
                  <p className="form-control-static">{group.requestedEffectiveDate ? new Date(group.requestedEffectiveDate).toLocaleDateString() : ""}</p>
                </div>
              </div>
              <div className="row form-group">
                <div className="col-sm-6 col-md-6 form-item">
                  <label className="control-label">Group Status</label>
                  <p className="form-control-static">{group.groupStatus}</p>
                </div>
                <div className="col-sm-6 col-md-6 form-item">
                  <label className="control-label">Enrollment Type</label>
                  <p className="form-control-static">{group.enrollmentRequestMethod}</p>
                </div>
              </div>
            </div>

            {/* Benefit Info */}
            <div style={sectionStyle}>
              <h2 style={sectionHeadingStyle}>Current Benefit Information</h2>
              <div className="row form-group">
                <div className="col-sm-6 col-md-6 form-item">
                  <label className="control-label">Plan Benefit Structure</label>
                  <p className="form-control-static">{group.planBenefitStructure}</p>
                </div>
                <div className="col-sm-6 col-md-6 form-item">
                  <label className="control-label">Total Premium</label>
                  <p className="form-control-static">{group.totalPremiumAmount != null ? `$${Number(group.totalPremiumAmount).toFixed(2)}` : ""}</p>
                </div>
              </div>
              <div className="row form-group">
                <div className="col-sm-6 col-md-6 form-item">
                  <label className="control-label">Billing Method</label>
                  <p className="form-control-static">{group.paymentOption}</p>
                </div>
                <div className="col-sm-6 col-md-6 form-item">
                  <label className="control-label">Open Enrollment Date</label>
                  <p className="form-control-static">{group.openEnrollmentStartDate ? new Date(group.openEnrollmentStartDate).toLocaleDateString() : ""}</p>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div style={sectionStyle}>
              <h2 style={sectionHeadingStyle}>Actions</h2>
              <div style={actionsStyle}>
                <Button
                  type="button"
                  className="webform-button--next button js-form-submit form-submit btn-default btn"
                  //style={{ background: "#0078d4", color: "white" }}
                  onClick={() => navigate(`/GroupRenewal?groupNumber=${groupNumber}`)}
                >
                  Request Renewal
                </Button>
                <Button
                  type="button"
                  className="webform-button--next button js-form-submit form-submit btn-default btn"
                  style={{ background: "#dc2626", color: "white" }}
                  onClick={() => navigate(`/GroupTermination?groupNumber=${groupNumber}&planYear=${group.planYear}`)}
                >
                  Request Termination
                </Button>
                <Button
                  type="button"
                  className="webform-button--next button js-form-submit form-submit btn-default btn"
                  //style={{ background: "#6b7280", color: "white" }}
                  onClick={() => navigate(`/GroupPlanContactInfo?groupNumber=${groupNumber}`)}
                >
                  Manage Contacts
                </Button>
              </div>
            </div>

          </div>
        </section>
      </div>
    </div>
  );
};

export default GroupDashboard;
